import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "./api";
import { useAuth } from "./AuthContext";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
} from "@mui/material";

/** Robust Firestore timestamp -> YYYY-MM-DD */
function formatFirestoreDate(value) {
  if (!value) return "";

  if (value?.date) value = value.date;

  const seconds =
    value?.seconds ??
    value?._seconds ??
    value?._seconds?.toNumber?.() ??
    value?._seconds?.low ??
    null;

  if (seconds != null && Number.isFinite(Number(seconds))) {
    return new Date(Number(seconds) * 1000).toISOString().slice(0, 10);
  }

  if (typeof value === "string") {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }

  if (value instanceof Date && !isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  return "";
}

function isWithdrawn(row) {
  const w = row?.withdrewAt;
  if (!w) return false;
  const formatted = formatFirestoreDate(w);
  return !!formatted;
}

function makeProjectApiBase(projectId) {
  return `/projects/${encodeURIComponent(projectId)}`;
}

function csvEscape(value) {
  const s = value == null ? "" : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --------- Date helpers ----------
function parseISODateToUTC(isoDate) {
  // isoDate = YYYY-MM-DD
  if (!isoDate) return null;
  const [y, m, d] = String(isoDate).split("-").map((x) => Number(x));
  if (!y || !m || !d) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (isNaN(dt.getTime())) return null;
  return dt;
}
function addDaysISO(isoDate, days) {
  const dt = parseISODateToUTC(isoDate);
  if (!dt) return "";
  dt.setUTCDate(dt.getUTCDate() + Number(days || 0));
  return dt.toISOString().slice(0, 10);
}
function daysDiff(aISO, bISO) {
  // b - a (days)
  const a = parseISODateToUTC(aISO);
  const b = parseISODateToUTC(bISO);
  if (!a || !b) return null;
  const ms = b.getTime() - a.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function clampInt(v, min, max) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const i = Math.floor(n);
  if (i < min) return min;
  if (i > max) return max;
  return i;
}

// --------- Step config model (frontend) ----------
// projectConfig.stepsConfig: {
//   order: ["1","2",...],
//   rules: {
//     "1": { alias?: string, canSkip?: boolean, minWaitDays?: number, autoWithdrawAfterDays?: number },
//     ...
//   }
// }
// Backward compatible: falls back to stepAliases + stepCount (default 10)

function buildDefaultStepsConfig(stepCount = 10, stepAliases = {}) {
  const count = clampInt(stepCount, 1, 50) || 10;
  const order = Array.from({ length: count }, (_, i) => String(i + 1));
  const rules = {};
  for (let i = 1; i <= count; i++) {
    const key = String(i);
    rules[key] = {
      alias: stepAliases?.[key] || "",
      canSkip: false,
      minWaitDays: 0,
      autoWithdrawAfterDays: 0,
    };
  }
  return { order, rules };
}
function normalizeStepsConfigFromProject(projectConfig) {
  const aliases = projectConfig?.stepAliases || {};

  // ✅ server-native fields
  const serverOrder = Array.isArray(projectConfig?.stepOrder)
    ? projectConfig.stepOrder.map((x) => String(x))
    : null;

  const serverRules = projectConfig?.stepRules || {};

  if (serverOrder?.length) {
    const order = serverOrder;
    const rules = {};

    for (const k of order) {
      const r = serverRules?.[k] || {};
      rules[k] = {
        alias: aliases?.[k] ?? "",
        canSkip: Boolean(r?.canSkip ?? false),
        minWaitDays: clampInt(r?.minWaitDays ?? 0, 0, 3650) ?? 0,
        // ✅ map backend -> frontend name
        autoWithdrawAfterDays: clampInt(r?.maxDaysToReport ?? 0, 0, 3650) ?? 0,
      };
    }

    return { order, rules };
  }

  // fallback legacy/default
  const stepCount = projectConfig?.stepCount ?? 10;
  return buildDefaultStepsConfig(stepCount, aliases);
}

// --------- Row step date access using ORDER keys ----------
function getStepDateByKey(row, stepKey) {
  // stepKey is string like "1", "2" (stable)
  const fromMap = row?.steps?.[String(stepKey)];
  const val = fromMap?.date ? fromMap.date : fromMap;
  const formatted = formatFirestoreDate(val);
  if (formatted) return formatted;

  // legacy direct field paths (if any)
  const legacyKey = `steps.${String(stepKey)}`;
  const legacyVal = row?.[legacyKey];
  const formattedLegacy = formatFirestoreDate(legacyVal);
  if (formattedLegacy) return formattedLegacy;

  return "";
}

function computePrevNextDatesInOrder(row, order, stepKey) {
  const idx = order.indexOf(String(stepKey));
  const prevKey = idx > 0 ? order[idx - 1] : null;
  const nextKey = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;

  const prevDate = prevKey ? getStepDateByKey(row, prevKey) : "";
  const nextDate = nextKey ? getStepDateByKey(row, nextKey) : "";

  return { idx, prevKey, nextKey, prevDate, nextDate };
}

export default function Dashboard() {
  const { user, logout } = useAuth();

  // data
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [projectConfig, setProjectConfig] = useState(null);

  const selectedProject = projects.find(
    (p) => (p.projectId || p.id) === projectId
  );
  const selectedProjectName = selectedProject?.name || projectConfig?.name || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  // ---- steps config (variable count + reordering + rules)
  const [stepsConfig, setStepsConfig] = useState(() =>
    buildDefaultStepsConfig(10, {})
  );
  const stepOrder = stepsConfig.order || [];
  const stepRules = stepsConfig.rules || {};

  // -----------------------------
  // Looker Studio dashboards (public embeds)
  // -----------------------------
  const DASHBOARDS = useMemo(
    () => [
      {
        key: "percent",
        label: "Percent control chart",
        url: "https://lookerstudio.google.com/embed/reporting/128fd699-7b9f-4e4f-ad70-720ec106b2dc/page/JVRlF",
      },
      {
        key: "count",
        label: "Count control chart",
        url: "https://lookerstudio.google.com/embed/reporting/ee3e5c81-69bc-423e-9310-0c83fb9c4491/page/iVbkF",
      },
      {
        key: "dashboard",
        label: "Generic Dashboard",
        url: "https://lookerstudio.google.com/embed/reporting/2c862728-cfca-40ab-8df8-381f5d01be84/page/PAclF",
      },
    ],
    []
  );

  const [selectedDashboardKey, setSelectedDashboardKey] = useState("dashboard");

  const getIsDashboardsView = () => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get("view") === "dashboards";
    } catch {
      return false;
    }
  };

  const openDashboardsView = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("view", "dashboards");
      window.open(url.toString(), "_blank", "noopener,noreferrer");
    } catch {
      window.open("?view=dashboards", "_blank", "noopener,noreferrer");
    }
  };

  const closeDashboardsView = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("view");
      window.location.assign(url.toString());
    } catch {
      window.location.assign("/");
    }
  };

  const isDashboardsView = getIsDashboardsView();
  const selectedDashboard =
    DASHBOARDS.find((d) => d.key === selectedDashboardKey) || DASHBOARDS[0];

  // editing step cell
  // editing: { id: screeningId, stepKey: "1", minDateISO, maxDateISO, warnMinWaitDays, warnMinWaitMsg }
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");

  // withdraw
  const [editingWithdrawId, setEditingWithdrawId] = useState(null);
  const [withdrawValue, setWithdrawValue] = useState("");

  // studyId
  const [editingStudyId, setEditingStudyId] = useState(null);
  const [studyIdValue, setStudyIdValue] = useState("");

  // create project dialog
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [newProjectId, setNewProjectId] = useState("");
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectStepCount, setNewProjectStepCount] = useState(10);
  const [creatingProject, setCreatingProject] = useState(false);

  // screening dialog
  const [screeningDialogOpen, setScreeningDialogOpen] = useState(false);
  const [creatingScreening, setCreatingScreening] = useState(false);
  const [autoIncrement, setAutoIncrement] = useState(true);
  const [suggestedNextId, setSuggestedNextId] = useState("");
  const [newScreeningId, setNewScreeningId] = useState("");
  const [newStudyId, setNewStudyId] = useState("");
  const [lastCreatedId, setLastCreatedId] = useState(null);

  // alias edit dialog (click header)
  const [aliasDialogOpen, setAliasDialogOpen] = useState(false);
  const [aliasStepKey, setAliasStepKey] = useState(null);
  const [aliasValue, setAliasValue] = useState("");
  const [savingAlias, setSavingAlias] = useState(false);

  // step settings dialog (3 controls: canSkip, minWaitDays, autoWithdrawAfterDays)
  const [stepSettingsOpen, setStepSettingsOpen] = useState(false);
  const [stepSettingsKey, setStepSettingsKey] = useState(null);
  const [stepSettingsDraft, setStepSettingsDraft] = useState({
    canSkip: false,
    minWaitDays: 0,
    autoWithdrawAfterDays: 0,
  });
  const [savingStepSettings, setSavingStepSettings] = useState(false);

  // manage steps dialog (add/delete/reorder)
  const [manageStepsOpen, setManageStepsOpen] = useState(false);
  const [manageDraftOrder, setManageDraftOrder] = useState([]);
  const [manageAddCount, setManageAddCount] = useState(1);
  const [savingManageSteps, setSavingManageSteps] = useState(false);

  // delete dialogs
  const [deleteScreeningOpen, setDeleteScreeningOpen] = useState(false);
  const [deleteScreeningId, setDeleteScreeningId] = useState("");
  const [deletingScreening, setDeletingScreening] = useState(false);

  const [clearProjectOpen, setClearProjectOpen] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState("");
  const [clearingProject, setClearingProject] = useState(false);

  const [deleteProjectOpen, setDeleteProjectOpen] = useState(false);
  const [deleteProjectConfirmText, setDeleteProjectConfirmText] = useState("");
  const [deletingProject, setDeletingProject] = useState(false);

  const show = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 3500);
  };

  const getStepLabel = (stepKey, indexInOrder) => {
    const rule = stepRules?.[String(stepKey)];
    const alias = (rule?.alias || "").trim();
    if (alias) return alias;
    // fallback friendly name: "Step 1" based on visual order
    const idx = typeof indexInOrder === "number" ? indexInOrder : stepOrder.indexOf(String(stepKey));
    return `Step ${idx >= 0 ? idx + 1 : stepKey}`;
  };

  // -----------------------------
  // API: load projects + config + rows
  // -----------------------------
  const loadProjects = async (keepSelection = true) => {
    const data = await apiFetch(`/projects?limit=200`);
    const list = data.data || [];
    setProjects(list);

    if (!keepSelection) return;

    if (!projectId && list.length) {
      setProjectId(list[0].projectId || list[0].id);
    }
  };

  const loadProjectConfig = async (pid) => {
    if (!pid) return;
    try {
      const cfg = await apiFetch(`${makeProjectApiBase(pid)}`);
      const pc = cfg.data || null;
      setProjectConfig(pc);

      const normalized = normalizeStepsConfigFromProject(pc);
      setStepsConfig(normalized);
    } catch {
      setProjectConfig(null);
      setStepsConfig(buildDefaultStepsConfig(10, {}));
    }
  };

  const refresh = async (pid = projectId) => {
    if (!pid) return;
    setLoading(true);
    try {
      const data = await apiFetch(`${makeProjectApiBase(pid)}/redcap_data?limit=1000`);
      setRows(data.data || []);
    } catch (e) {
      show("err", e.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshSuggestedNextId = async (pid = projectId) => {
    if (!pid) return "";
    try {
      const data = await apiFetch(`${makeProjectApiBase(pid)}/suggest-next-screening-id`);
      setSuggestedNextId(data.suggestedScreeningId || "");
      return data.suggestedScreeningId || "";
    } catch {
      setSuggestedNextId("");
      return "";
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadProjects(true);
      } catch (e) {
        show("err", e.message);
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      await loadProjectConfig(projectId);
      await refresh(projectId);
      await refreshSuggestedNextId(projectId);
      setLastCreatedId(null);
    })();
    // eslint-disable-next-line
  }, [projectId]);

  // -----------------------------
  // Create project (dialog) - now asks step count
  // -----------------------------
  const openCreateProject = () => {
    setNewProjectId("");
    setNewProjectName("");
    setNewProjectStepCount(10);
    setProjectDialogOpen(true);
  };

  const createProject = async () => {
    const pid = (newProjectId || "").trim();
    const name = (newProjectName || "").trim();
    const stepCount = clampInt(newProjectStepCount, 1, 50) || 10;

    if (!pid) {
      show("err", "Please enter projectId");
      return;
    }

    setCreatingProject(true);
    try {
      const initialStepsConfig = buildDefaultStepsConfig(stepCount, {});
      await apiFetch(`/projects`, {
        method: "POST",
        body: {
          projectId: pid,
          ...(name ? { name } : {}),
          stepCount,
          stepsConfig: initialStepsConfig,
        },
      });

      show("ok", `Created project ${pid}`);
      setProjectDialogOpen(false);

      const data = await apiFetch(`/projects?limit=200`);
      const list = data.data || [];
      setProjects(list);
      setProjectId(pid);
    } catch (e) {
      show("err", e.message);
    } finally {
      setCreatingProject(false);
    }
  };

  // -----------------------------
  // Create screening (dialog)
  // -----------------------------
  const openCreateScreening = async () => {
    if (!projectId) {
      show("err", "Select a project first");
      return;
    }

    setNewStudyId("");
    setNewScreeningId("");
    setAutoIncrement(true);

    const next = await refreshSuggestedNextId(projectId);
    setNewScreeningId(next || "");

    setScreeningDialogOpen(true);
  };

  const createNewScreening = async () => {
    if (!projectId) return;

    const sid = (newScreeningId || "").trim();
    if (!sid) {
      show("err", "Please enter screeningId");
      return;
    }

    setCreatingScreening(true);
    try {
      const body = { screeningId: sid };
      if ((newStudyId || "").trim()) body.studyId = newStudyId.trim();

      const data = await apiFetch(`${makeProjectApiBase(projectId)}/redcap_data`, {
        method: "POST",
        body,
      });

      const created = data.screeningId || sid;
      setLastCreatedId(created);
      show("ok", `Created screeningId ${created}`);

      setScreeningDialogOpen(false);
      await refresh(projectId);
      await refreshSuggestedNextId(projectId);
    } catch (e) {
      show("err", e.message);
    } finally {
      setCreatingScreening(false);
    }
  };

  useEffect(() => {
    if (!screeningDialogOpen) return;
    if (autoIncrement) {
      setNewScreeningId(suggestedNextId || newScreeningId);
    }
    // eslint-disable-next-line
  }, [autoIncrement, suggestedNextId, screeningDialogOpen]);

  // -----------------------------
  // Persist project config helpers (stepsConfig + stepAliases compatibility)
  // -----------------------------
  const saveProjectStepsConfig = async (nextStepsConfig) => {
    if (!projectId) return;

    // aliases (keep as you had)
    const nextAliases = {};
    for (const k of nextStepsConfig.order) {
      const alias = (nextStepsConfig.rules?.[k]?.alias || "").trim();
      if (alias) nextAliases[k] = alias;
    }

    // ✅ build backend stepRules
    const nextStepRules = {};
    for (const k of nextStepsConfig.order) {
      const r = nextStepsConfig.rules?.[k] || {};
      const aw = clampInt(r.autoWithdrawAfterDays ?? 0, 0, 3650) ?? 0;

      nextStepRules[k] = {
        canSkip: Boolean(r.canSkip),
        minWaitDays: clampInt(r.minWaitDays ?? 0, 0, 3650) ?? 0,
        // ✅ backend expects maxDaysToReport (null = disabled)
        maxDaysToReport: aw > 0 ? aw : null,
      };
    }

    await apiFetch(`${makeProjectApiBase(projectId)}`, {
      method: "PUT",
      body: {
        stepAliases: nextAliases,
        stepRules: nextStepRules,
        // NOTE: do NOT send stepOrder here unless you're ONLY reordering with same keys
      },
    });

    // optimistic local update so UI stays in sync
    setProjectConfig((prev) => ({
      ...(prev || { projectId }),
      stepAliases: nextAliases,
      stepRules: nextStepRules,
    }));
    setStepsConfig(nextStepsConfig);
  };

  // -----------------------------
  // Alias editing (click header title)
  // -----------------------------
  const openAliasEdit = (stepKey) => {
    if (!projectId) {
      show("err", "Select a project first");
      return;
    }
    setAliasStepKey(String(stepKey));
    setAliasValue(stepRules?.[String(stepKey)]?.alias || "");
    setAliasDialogOpen(true);
  };

  const saveAlias = async () => {
    if (!projectId || !aliasStepKey) return;
    const label = (aliasValue || "").trim();

    const next = {
      ...stepsConfig,
      rules: {
        ...(stepsConfig.rules || {}),
        [String(aliasStepKey)]: {
          ...(stepsConfig.rules?.[String(aliasStepKey)] || {}),
          alias: label,
        },
      },
    };

    setSavingAlias(true);
    try {
      await saveProjectStepsConfig(next);
      show("ok", `Updated ${label ? "alias" : "alias cleared"}`);
      setAliasDialogOpen(false);
    } catch (e) {
      show("err", e.message);
    } finally {
      setSavingAlias(false);
    }
  };

  // -----------------------------
  // Step Settings (header "⋯" menu -> dialog)
  // -----------------------------
  const openStepSettings = (stepKey) => {
    if (!projectId) {
      show("err", "Select a project first");
      return;
    }
    const k = String(stepKey);
    const r = stepRules?.[k] || {};
    setStepSettingsKey(k);
    setStepSettingsDraft({
      canSkip: Boolean(r.canSkip),
      minWaitDays: clampInt(r.minWaitDays ?? 0, 0, 3650) ?? 0,
      autoWithdrawAfterDays:
        clampInt(r.autoWithdrawAfterDays ?? 0, 0, 3650) ?? 0,
    });
    setStepSettingsOpen(true);
  };

  const saveStepSettings = async () => {
    if (!projectId || !stepSettingsKey) return;

    const k = String(stepSettingsKey);
    const next = {
      ...stepsConfig,
      rules: {
        ...(stepsConfig.rules || {}),
        [k]: {
          ...(stepsConfig.rules?.[k] || {}),
          canSkip: Boolean(stepSettingsDraft.canSkip),
          minWaitDays: clampInt(stepSettingsDraft.minWaitDays ?? 0, 0, 3650) ?? 0,
          autoWithdrawAfterDays:
            clampInt(stepSettingsDraft.autoWithdrawAfterDays ?? 0, 0, 3650) ?? 0,
        },
      },
    };

    setSavingStepSettings(true);
    try {
      await saveProjectStepsConfig(next);
      show("ok", `Saved step settings`);
      setStepSettingsOpen(false);
    } catch (e) {
      show("err", e.message);
    } finally {
      setSavingStepSettings(false);
    }
  };

  // -----------------------------
  // Manage Steps (add/delete/reorder)
  // -----------------------------
  const openManageSteps = () => {
    if (!projectId) {
      show("err", "Select a project first");
      return;
    }
    setManageDraftOrder([...stepOrder]);
    setManageAddCount(1);
    setManageStepsOpen(true);
  };

  // drag reorder (simple HTML5 DnD)
  const dragKeyRef = useRef(null);
  const onDragStart = (k) => (e) => {
    dragKeyRef.current = String(k);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (k) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDrop = (k) => (e) => {
    e.preventDefault();
    const from = dragKeyRef.current;
    const to = String(k);
    if (!from || from === to) return;

    setManageDraftOrder((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(from);
      const toIdx = next.indexOf(to);
      if (fromIdx < 0 || toIdx < 0) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, from);
      return next;
    });
  };

  const addStepsToDraft = () => {
    const count = clampInt(manageAddCount, 1, 20) || 1;
    setManageDraftOrder((prev) => {
      // find next numeric key not used
      const used = new Set(prev.map(String));
      let maxNum = 0;
      for (const k of used) {
        const n = Number(k);
        if (Number.isFinite(n)) maxNum = Math.max(maxNum, n);
      }
      const next = [...prev];
      for (let i = 0; i < count; i++) {
        maxNum += 1;
        next.push(String(maxNum));
      }
      return next;
    });
  };

  const deleteStepFromDraft = (k) => {
    const key = String(k);
    setManageDraftOrder((prev) => prev.filter((x) => String(x) !== key));
  };

  // rule: if adding steps, "make sure they report all values before continue"
  // Interpreting safely: do not allow adding/reordering if any existing row has "gaps"
  // (missing a step date for any step that already has a later step date).
  const findFirstGapViolation = (orderToValidate) => {
    for (const row of rows) {
      if (isWithdrawn(row)) continue;

      let seenLaterDate = "";
      for (let i = orderToValidate.length - 1; i >= 0; i--) {
        const k = orderToValidate[i];
        const d = getStepDateByKey(row, k);
        if (d) {
          seenLaterDate = d;
        } else {
          // missing this step, but later exists => gap
          if (seenLaterDate) {
            return {
              screeningId: row.screeningId,
              stepKey: k,
              laterDate: seenLaterDate,
            };
          }
        }
      }
    }
    return null;
  };

  const saveManageSteps = async () => {
    if (!projectId) return;

    const nextOrder = manageDraftOrder.map(String).filter(Boolean);
    if (!nextOrder.length) {
      show("err", "You must have at least 1 step.");
      return;
    }

    // adding new steps?
    const prevSet = new Set(stepOrder.map(String));
    const nextSet = new Set(nextOrder.map(String));
    let isAdding = false;
    for (const k of nextSet) {
      if (!prevSet.has(k)) {
        isAdding = true;
        break;
      }
    }

    if (isAdding) {
      const violation = findFirstGapViolation(stepOrder);
      if (violation) {
        show(
          "err",
          `Cannot add steps yet. Screening ${violation.screeningId} has a gap: missing step "${getStepLabel(
            violation.stepKey
          )}" but later step has date ${violation.laterDate}. Fill missing steps first.`
        );
        return;
      }
    }

    // build next stepsConfig:
    const nextRules = { ...(stepsConfig.rules || {}) };

    // ensure rule entries for new keys
    for (const k of nextOrder) {
      if (!nextRules[k]) {
        nextRules[k] = {
          alias: "",
          canSkip: false,
          minWaitDays: 0,
          autoWithdrawAfterDays: 0,
        };
      } else {
        nextRules[k] = {
          alias: (nextRules[k].alias || "").trim(),
          canSkip: Boolean(nextRules[k].canSkip),
          minWaitDays: clampInt(nextRules[k].minWaitDays ?? 0, 0, 3650) ?? 0,
          autoWithdrawAfterDays:
            clampInt(nextRules[k].autoWithdrawAfterDays ?? 0, 0, 3650) ?? 0,
        };
      }
    }

    // deleted steps have no impact (we keep rules around harmlessly, or you can prune)
    const nextStepsConfig = { order: nextOrder, rules: nextRules };

    setSavingManageSteps(true);
    try {
      await saveProjectStepsConfig(nextStepsConfig);
      show("ok", "Updated steps");
      setManageStepsOpen(false);
    } catch (e) {
      show("err", e.message);
    } finally {
      setSavingManageSteps(false);
    }
  };

  // -----------------------------
  // Step editing with validation + warnings
  // -----------------------------
  const startEdit = (row, stepKey) => {
    if (isWithdrawn(row)) return;

    const { prevDate, nextDate } = computePrevNextDatesInOrder(
      row,
      stepOrder,
      String(stepKey)
    );

    const rule = stepRules?.[String(stepKey)] || {};
    const minWaitDays = clampInt(rule.minWaitDays ?? 0, 0, 3650) ?? 0;

    // lock frontend min date:
    // - cannot be before prevDate
    // - and if minWaitDays set, also cannot be before prevDate + minWaitDays (lock)
    let minDateISO = "";
    if (prevDate) {
      minDateISO = prevDate;
      if (minWaitDays > 0) {
        const lock = addDaysISO(prevDate, minWaitDays);
        if (lock && lock > minDateISO) minDateISO = lock;
      }
    }

    // cannot be after nextDate (hard constraint)
    const maxDateISO = nextDate || "";

    setEditing({
      id: row.screeningId,
      stepKey: String(stepKey),
      minDateISO,
      maxDateISO,
      warnMinWaitDays: minWaitDays,
      warnMinWaitMsg:
        prevDate && minWaitDays > 0
          ? `You set a ${minWaitDays}-day wait. Locked earliest date is ${addDaysISO(
            prevDate,
            minWaitDays
          )}.`
          : "",
    });
    setEditValue(getStepDateByKey(row, stepKey) || "");
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditValue("");
  };

  const saveEdit = async () => {
    if (!editing || !projectId) return;
    const { id, stepKey } = editing;
    const date = editValue;

    if (!date) {
      cancelEdit();
      return;
    }

    const currentRow = rows.find((r) => r.screeningId === id);
    if (!currentRow) {
      show("err", "Row not found (refresh?)");
      cancelEdit();
      return;
    }

    // compute prev/next in current order
    const { prevKey, nextKey, prevDate, nextDate } = computePrevNextDatesInOrder(
      currentRow,
      stepOrder,
      String(stepKey)
    );

    // Hard constraints:
    // 1) cannot go before prevDate
    if (prevDate && date < prevDate) {
      show(
        "err",
        `${getStepLabel(stepKey)} cannot be before previous step (${getStepLabel(
          prevKey
        )}) date ${prevDate}.`
      );
      return;
    }

    // 2) cannot go after nextDate (if next already set)
    if (nextDate && date > nextDate) {
      show(
        "err",
        `${getStepLabel(stepKey)} cannot be after next step (${getStepLabel(
          nextKey
        )}) date ${nextDate}.`
      );
      return;
    }

    // 3) ensure overall "days increase not go back" across whole row
    // Validate monotonic: as we traverse order, dates must be non-decreasing (and realistically increasing)
    const temp = { ...currentRow, steps: { ...(currentRow.steps || {}) } };
    temp.steps[String(stepKey)] = { ...(temp.steps[String(stepKey)] || {}), date };

    let lastSeen = "";
    for (const k of stepOrder) {
      const d = getStepDateByKey(temp, k);
      if (!d) continue;
      if (lastSeen && d < lastSeen) {
        show(
          "err",
          `Invalid timeline: ${getStepLabel(k)} (${d}) is before an earlier step date (${lastSeen}). Dates must not go backwards.`
        );
        return;
      }
      lastSeen = d;
    }

    // Soft warning: minWaitDays (but your request also said lock in frontend; we already set min attribute)
    // Still warn if they somehow bypass UI (e.g. manual typing).
    const rule = stepRules?.[String(stepKey)] || {};
    const minWaitDays = clampInt(rule.minWaitDays ?? 0, 0, 3650) ?? 0;
    if (prevDate && minWaitDays > 0) {
      const diff = daysDiff(prevDate, date);
      if (diff != null && diff < minWaitDays) {
        show(
          "err",
          `You set a ${minWaitDays}-day wait from ${getStepLabel(
            prevKey
          )}. You are reporting after ${diff} days (earlier than configured).`
        );
        // Per your spec: "let them report before 15 days but just say ..."
        // So we DO NOT block here.
      }
    }

    try {
      await apiFetch(
        `${makeProjectApiBase(projectId)}/redcap_data/${encodeURIComponent(id)}/step`,
        {
          method: "POST",
          body: { stepKey: String(stepKey), date },
        }
      );

      // Optimistic update
      setRows((prev) =>
        prev.map((r) => {
          if (r.screeningId !== id) return r;
          return {
            ...r,
            steps: {
              ...(r.steps || {}),
              [String(stepKey)]: {
                ...(r.steps?.[String(stepKey)] || {}),
                date,
              },
            },
          };
        })
      );

      show("ok", `Saved ${getStepLabel(stepKey)} for ${id}`);
    } catch (e) {
      show("err", e.message);
    } finally {
      cancelEdit();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveEdit();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  // -----------------------------
  // Withdraw
  // -----------------------------
  const startWithdrawEdit = (row) => {
    if (isWithdrawn(row)) return;
    setEditingWithdrawId(row.screeningId);
    setWithdrawValue("");
  };

  const cancelWithdrawEdit = () => {
    setEditingWithdrawId(null);
    setWithdrawValue("");
  };

  const saveWithdraw = async () => {
    if (!editingWithdrawId || !projectId) return;
    if (!withdrawValue) {
      cancelWithdrawEdit();
      return;
    }

    const id = editingWithdrawId;

    try {
      await apiFetch(
        `${makeProjectApiBase(projectId)}/redcap_data/${encodeURIComponent(id)}/withdraw`,
        {
          method: "POST",
          body: { date: withdrawValue },
        }
      );

      setRows((prev) =>
        prev.map((r) => (r.screeningId !== id ? r : { ...r, withdrewAt: withdrawValue }))
      );
      show("ok", `Withdrawn ${id}`);
    } catch (e) {
      show("err", e.message);
    } finally {
      cancelWithdrawEdit();
    }
  };

  const handleWithdrawKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveWithdraw();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      cancelWithdrawEdit();
    }
  };

  const revertWithdraw = async (row) => {
    if (!projectId) return;
    const id = row.screeningId;
    try {
      await apiFetch(
        `${makeProjectApiBase(projectId)}/redcap_data/${encodeURIComponent(id)}/revert-withdraw`,
        { method: "POST" }
      );

      setRows((prev) =>
        prev.map((r) => (r.screeningId !== id ? r : { ...r, withdrewAt: null }))
      );
      show("ok", `Reverted withdraw for ${id}`);
    } catch (e) {
      show("err", e.message);
    }
  };

  // -----------------------------
  // StudyId
  // -----------------------------
  const startStudyIdEdit = (row) => {
    setEditingStudyId(row.screeningId);
    setStudyIdValue(row.studyId || "");
  };

  const cancelStudyIdEdit = () => {
    setEditingStudyId(null);
    setStudyIdValue("");
  };

  const saveStudyId = async () => {
    if (!editingStudyId || !projectId) return;
    const id = editingStudyId;

    try {
      await apiFetch(
        `${makeProjectApiBase(projectId)}/redcap_data/${encodeURIComponent(id)}/meta`,
        {
          method: "PATCH",
          body: { studyId: studyIdValue },
        }
      );

      setRows((prev) =>
        prev.map((r) =>
          r.screeningId !== id ? r : { ...r, studyId: studyIdValue || undefined }
        )
      );
      show("ok", `Saved studyId for ${id}`);
    } catch (e) {
      show("err", e.message);
    } finally {
      cancelStudyIdEdit();
    }
  };

  const handleStudyIdKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveStudyId();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      cancelStudyIdEdit();
    }
  };

  // -----------------------------
  // Auto-withdraw hint (frontend-only display)
  // If a step has autoWithdrawAfterDays and prev step date exists but current step date missing
  // and elapsed days > threshold => show a warning chip. (Backend should enforce actual withdraw.)
  // -----------------------------
  const getAutoWithdrawStatus = (row, stepKey) => {
    const rule = stepRules?.[String(stepKey)];
    const aw = clampInt(rule?.autoWithdrawAfterDays ?? 0, 0, 3650) ?? 0;
    if (!aw) return null;

    const { prevDate } = computePrevNextDatesInOrder(row, stepOrder, String(stepKey));
    const curDate = getStepDateByKey(row, stepKey);
    if (!prevDate || curDate) return null;

    const diff = daysDiff(prevDate, new Date().toISOString().slice(0, 10));
    if (diff != null && diff > aw) {
      return { overdueBy: diff - aw, threshold: aw };
    }
    return null;
  };

  // -----------------------------
  // CSV (uses visual order)
  // -----------------------------
  const downloadCsv = () => {
    if (!projectId) {
      show("err", "Select a project first");
      return;
    }

    const headers = [
      "screeningId",
      "studyId",
      "status",
      "withdrewAt",
      ...stepOrder.map((k, idx) => getStepLabel(k, idx)),
    ];
    const lines = [headers.map(csvEscape).join(",")];

    const sorted = [...(rows || [])].sort((a, b) => {
      const an = Number(a.screeningIdNum || 0);
      const bn = Number(b.screeningIdNum || 0);
      if (an && bn) return an - bn;
      return String(a.screeningId || "").localeCompare(String(b.screeningId || ""));
    });

    for (const row of sorted) {
      const withdrawn = isWithdrawn(row);
      const status = withdrawn ? "Withdrawn" : "Active";
      const withdrewAt = formatFirestoreDate(row.withdrewAt);

      const stepValues = stepOrder.map((k) => getStepDateByKey(row, k));

      // "skip" behavior request: if canSkip selected for step, ignore that step and report next column,
      // using the SAME date entered in next column to the skipped ones.
      // We can apply this in export too (it’s harmless):
      // For any step i with canSkip=true and empty date, if a later step has date, fill forward.
      const exported = [...stepValues];
      for (let i = 0; i < stepOrder.length - 1; i++) {
        const k = stepOrder[i];
        const r = stepRules?.[k];
        if (!r?.canSkip) continue;
        if (exported[i]) continue;

        // find next non-empty
        for (let j = i + 1; j < stepOrder.length; j++) {
          if (exported[j]) {
            exported[i] = exported[j];
            break;
          }
        }
      }

      const record = [row.screeningId ?? "", row.studyId ?? "", status, withdrewAt, ...exported];
      lines.push(record.map(csvEscape).join(","));
    }

    const filenameSafeProject = String(projectId).replace(/[^a-zA-Z0-9_-]/g, "_");
    const iso = new Date().toISOString().slice(0, 10);
    downloadTextFile(`redcap_${filenameSafeProject}_${iso}.csv`, lines.join("\n"));
    show("ok", "CSV downloaded");
  };

  // -----------------------------
  // Deletes
  // -----------------------------
  const openDeleteScreening = (sid) => {
    if (!projectId) {
      show("err", "Select a project first");
      return;
    }
    setDeleteScreeningId(String(sid || ""));
    setDeleteScreeningOpen(true);
  };

  const confirmDeleteScreening = async () => {
    if (!projectId || !deleteScreeningId) return;
    setDeletingScreening(true);
    try {
      await apiFetch(`${makeProjectApiBase(projectId)}/redcap_data/${encodeURIComponent(deleteScreeningId)}`, {
        method: "DELETE",
      });

      setRows((prev) => prev.filter((r) => String(r.screeningId) !== String(deleteScreeningId)));
      show("ok", `Deleted screening ${deleteScreeningId}`);
      setDeleteScreeningOpen(false);
      setDeleteScreeningId("");
    } catch (e) {
      show("err", e.message);
    } finally {
      setDeletingScreening(false);
    }
  };

  const openClearProject = () => {
    if (!projectId) {
      show("err", "Select a project first");
      return;
    }
    setClearConfirmText("");
    setClearProjectOpen(true);
  };

  const confirmClearProject = async () => {
    if (!projectId) return;
    if (clearConfirmText.trim().toUpperCase() !== "CLEAR") {
      show("err", "Type CLEAR to confirm");
      return;
    }

    setClearingProject(true);
    try {
      await apiFetch(`${makeProjectApiBase(projectId)}/redcap_data?confirm=YES`, { method: "DELETE" });

      setRows([]);
      setLastCreatedId(null);
      show("ok", `Cleared all screenings in ${projectId}`);
      setClearProjectOpen(false);
      setClearConfirmText("");
      await refreshSuggestedNextId(projectId);
    } catch (e) {
      show("err", e.message);
    } finally {
      setClearingProject(false);
    }
  };

  const openDeleteProject = () => {
    if (!projectId) {
      show("err", "Select a project first");
      return;
    }
    setDeleteProjectConfirmText("");
    setDeleteProjectOpen(true);
  };

  const confirmDeleteProject = async () => {
    if (!projectId) return;
    if (deleteProjectConfirmText.trim().toUpperCase() !== "DELETE") {
      show("err", "Type DELETE to confirm");
      return;
    }

    setDeletingProject(true);
    const pid = projectId;
    try {
      await apiFetch(`${makeProjectApiBase(pid)}?confirm=YES`, { method: "DELETE" });

      show("ok", `Deleted project ${pid}`);

      const data = await apiFetch(`/projects?limit=200`);
      const list = data.data || [];
      setProjects(list);

      const next = list.length ? list[0].projectId || list[0].id : "";
      setProjectId(next);
      setRows([]);
      setProjectConfig(null);
      setLastCreatedId(null);

      setDeleteProjectOpen(false);
      setDeleteProjectConfirmText("");
    } catch (e) {
      show("err", e.message);
    } finally {
      setDeletingProject(false);
    }
  };

  // -----------------------------
  // Styles: your token buttons + consistent dialogs
  // -----------------------------
  const btnPrimary =
    "rounded-lg px-3 py-2 text-sm font-semibold bg-gradient-to-r from-button to-primary text-white shadow-md hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed";
  const btnSecondary =
    "rounded-lg px-3 py-2 text-sm font-semibold border border-white/50 bg-white/60 text-textsec shadow-sm hover:bg-white/70 disabled:opacity-60 disabled:cursor-not-allowed";
  const btnSecondaryColored =
    "rounded-lg px-3 py-2 text-sm font-semibold bg-gradient-to-r from-primary/25 to-button/20 text-primary shadow-sm hover:opacity-95 border border-primary/25 disabled:opacity-60 disabled:cursor-not-allowed";
  const btnDanger =
    "rounded-lg px-3 py-2 text-sm font-semibold bg-primary text-white shadow-md hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed";
  const btnDangerOutline =
    "rounded-lg px-3 py-2 text-sm font-semibold border border-primary/40 bg-white/60 text-primary shadow-sm hover:bg-white/70 disabled:opacity-60 disabled:cursor-not-allowed";

  const dialogPaperClass =
    "rounded-3xl overflow-hidden border border-white/50 bg-white/75 backdrop-blur-xl shadow-2xl";
  const dialogTitleClass =
    "bg-gradient-to-r from-primary/20 to-white/10 border-b border-white/30";
  const dialogActionsClass = "bg-white/40 border-t border-white/30";

  const btnDialogPrimary =
    "rounded-lg px-3 py-1.5 text-sm font-semibold bg-gradient-to-r from-button to-primary text-white shadow-md hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed";
  const btnDialogSecondary =
    "rounded-lg px-3 py-1.5 text-sm font-semibold border border-white/50 bg-white/60 text-textsec shadow-sm hover:bg-white/70 disabled:opacity-60 disabled:cursor-not-allowed";
  const btnDialogDanger =
    "rounded-lg px-3 py-1.5 text-sm font-semibold bg-gradient-to-r from-primary to-button text-white shadow-md hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed";

  const btnRowPrimary =
    "rounded-lg px-2.5 py-1 text-xs font-semibold text-white shadow-md bg-gradient-to-r from-button to-primary hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed";
  const btnRowDanger =
    "rounded-lg px-2.5 py-1 text-xs font-semibold text-white shadow-md bg-gradient-to-r from-primary to-button hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed";

  // -----------------------------
  // Dashboards view
  // -----------------------------
  if (isDashboardsView) {
    return (
      <div className="min-h-screen relative overflow-hidden font-roboto">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary via-back to-tertiary" />
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-28 -right-28 h-96 w-96 rounded-full bg-button/25 blur-3xl" />

        <div className="relative max-w-[1200px] mx-auto px-4 py-6">
          <div className="rounded-2xl bg-white/60 border border-white/50 shadow-sm p-4 mb-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="inline-flex items-center justify-center rounded-2xl px-4 py-2 bg-white/60 border border-white/50 shadow-sm">
                  <span className="font-blackOpsOne text-primary text-xl tracking-wide">
                    Operational Analytics
                  </span>
                </div>
                <div className="mt-2 text-sm text-textsec">
                  Logged in as <span className="font-medium">{user?.email || "user"}</span>
                </div>
                <div className="mt-1 text-xs text-textsec/70">
                  Select a dashboard from the dropdown to view it.
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={closeDashboardsView} className={btnSecondary}>
                  Back
                </button>
                <button onClick={logout} className={btnDanger}>
                  Logout
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-white/60 border border-white/50 shadow-sm p-4 mb-4">
            <div className="text-sm font-semibold text-textsec mb-3">Dashboard</div>

            <FormControl size="small" fullWidth>
              <InputLabel id="dashboard-select-label">Select dashboard</InputLabel>
              <Select
                labelId="dashboard-select-label"
                label="Select dashboard"
                value={selectedDashboardKey}
                onChange={(e) => setSelectedDashboardKey(e.target.value)}
              >
                {DASHBOARDS.map((d) => (
                  <MenuItem key={d.key} value={d.key}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <div className="mt-3 text-xs text-textsec/70">
              Opening public Looker Studio embed:{" "}
              <span className="font-medium">{selectedDashboard?.label}</span>
            </div>
          </div>

          <div
            className="rounded-2xl bg-white/60 border border-white/50 shadow-sm overflow-hidden"
            style={{ width: "100%" }}
          >
            <iframe
              title={selectedDashboard?.label || "Dashboard"}
              width="100%"
              height="800"
              src={selectedDashboard?.url}
              frameBorder="0"
              style={{ border: 0 }}
              allowFullScreen
              sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            />
          </div>

          {msg.text ? (
            <div
              className={
                "mt-4 rounded-xl border px-4 py-3 text-sm shadow-sm " +
                (msg.type === "ok"
                  ? "border-c3/40 bg-c3/10 text-textsec"
                  : "border-c1/40 bg-c1/10 text-primary")
              }
            >
              {msg.text}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // -----------------------------
  // Main UI
  // -----------------------------
  return (
    <div className="min-h-screen relative overflow-hidden font-roboto">
      <div className="absolute inset-0 bg-gradient-to-br from-secondary via-back to-tertiary" />
      <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
      <div className="absolute -bottom-28 -right-28 h-96 w-96 rounded-full bg-button/25 blur-3xl" />

      <div className="relative max-w-[1600px] mx-auto px-4 py-6">
        {/* Header + Controls */}
        <div className="flex flex-col gap-4 mb-4">
          {/* Title card */}
          <div className="rounded-2xl bg-white/60 border border-white/50 shadow-sm p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="inline-flex items-center justify-center rounded-2xl px-4 py-2 bg-white/60 border border-white/50 shadow-sm">
                  <span className="font-blackOpsOne text-primary text-xl tracking-wide">
                    Agile GPS Dashboard
                  </span>
                </div>

                <div className="mt-2 text-sm text-textsec">
                  {projectId ? (
                    <>
                      Project:{" "}
                      <span className="font-semibold">
                        {selectedProjectName
                          ? `${selectedProjectName} (${projectId})`
                          : projectId}
                      </span>
                      <span className="mx-2">•</span>
                      Steps: <span className="font-semibold">{stepOrder.length}</span>
                    </>
                  ) : (
                    "Select a project"
                  )}
                  <span className="mx-2">•</span>
                  Logged in as{" "}
                  <span className="font-medium">{user?.email || "user"}</span>
                  {lastCreatedId ? (
                    <>
                      <span className="mx-2">•</span>
                      New screeningId:{" "}
                      <span className="font-semibold text-primary">{lastCreatedId}</span>
                    </>
                  ) : null}
                </div>

                <div className="mt-1 text-xs text-textsec/70">
                  Click a date cell to edit. Header: click name to rename; use ⚙ to set skip/timeline rules; Manage Steps to add/delete/reorder.
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={openDashboardsView} className={btnSecondaryColored}>
                  View Generic Dashboard
                </button>
                <button onClick={logout} className={btnDanger}>
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-col gap-3">
            {/* Project section */}
            <div className="rounded-2xl bg-white/60 border border-white/50 shadow-sm p-4">
              <div className="text-sm font-semibold text-textsec mb-3">Project</div>

              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <FormControl size="small" fullWidth sx={{ maxWidth: 1000 }}>
                  <InputLabel id="project-select-label">Project</InputLabel>
                  <Select
                    labelId="project-select-label"
                    label="Project"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                  >
                    {projects.map((p) => {
                      const id = p.projectId || p.id;
                      return (
                        <MenuItem key={id} value={id}>
                          {p.name ? `${p.name} (${id})` : id}
                        </MenuItem>
                      );
                    })}
                    {!projects.length ? (
                      <MenuItem value="" disabled>
                        No projects
                      </MenuItem>
                    ) : null}
                  </Select>
                </FormControl>

                <div className="flex gap-2 flex-wrap">
                  <button onClick={openCreateProject} className={btnPrimary}>
                    Add Project
                  </button>

                  <button
                    onClick={openManageSteps}
                    disabled={!projectId || loading}
                    className={btnSecondaryColored}
                  >
                    Manage Steps
                  </button>

                  <button
                    onClick={openDeleteProject}
                    disabled={!projectId || loading}
                    className={btnDangerOutline}
                  >
                    Delete Project
                  </button>
                </div>
              </div>

              <div className="mt-2 text-xs text-textsec/70">
                Each project has its own screenings + step order + step rules.
              </div>
            </div>

            {/* Screening section (only after project selected) */}
            {projectId ? (
              <div className="rounded-2xl bg-white/60 border border-white/50 shadow-sm p-4">
                <div className="text-sm font-semibold text-textsec mb-3">
                  Screening data
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={openCreateScreening} className={btnPrimary}>
                      Add New Screening Data
                    </button>

                    <button onClick={downloadCsv} disabled={loading} className={btnPrimary}>
                      Download CSV
                    </button>

                    <button
                      onClick={() => refresh(projectId)}
                      disabled={loading}
                      className={btnSecondaryColored}
                    >
                      {loading ? "Loading..." : "Refresh"}
                    </button>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <button onClick={openClearProject} disabled={loading} className={btnDangerOutline}>
                      Clear Data
                    </button>
                  </div>
                </div>

                <div className="mt-2 text-xs text-textsec/70">
                  Clear Data removes all screenings in this project (project stays).
                </div>
              </div>
            ) : null}
          </div>

          {/* Toast */}
          {msg.text ? (
            <div
              className={
                "rounded-xl border px-4 py-3 text-sm shadow-sm " +
                (msg.type === "ok"
                  ? "border-c3/40 bg-c3/10 text-textsec"
                  : "border-c1/40 bg-c1/10 text-primary")
              }
            >
              {msg.text}
            </div>
          ) : null}
        </div>

        {/* Table */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            background: "rgba(255,255,255,0.60)",
            border: "1px solid rgba(255,255,255,0.55)",
            borderRadius: 2,
            overflow: "auto",
            maxHeight: "calc(100vh - 260px)",
          }}
        >
          <Table size="small" stickyHeader sx={{ minWidth: 1600 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>
                  screeningId
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>
                  studyId
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 90 }}>
                  status
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>
                  withdrewAt
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>
                  actions
                </TableCell>

                {stepOrder.map((k, idx) => {
                  const rule = stepRules?.[k] || {};
                  return (
                    <TableCell
                      key={k}
                      sx={{
                        fontWeight: 800,
                        minWidth: 190,
                        userSelect: "none",
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <Tooltip title="Click to rename this step">
                          <span
                            onClick={() => openAliasEdit(k)}
                            style={{ cursor: projectId ? "pointer" : "default" }}
                          >
                            {getStepLabel(k, idx)}
                          </span>
                        </Tooltip>

                        <div className="flex items-center gap-1">
                          {/* tiny badges to show rule states */}
                          {rule?.canSkip ? (
                            <Tooltip title="Skip enabled for this step">
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/70 border border-white/50 text-textsec">
                                Skip
                              </span>
                            </Tooltip>
                          ) : null}
                          {Number(rule?.minWaitDays || 0) > 0 ? (
                            <Tooltip title={`Min wait: ${rule.minWaitDays} days`}>
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/70 border border-white/50 text-textsec">
                                +{rule.minWaitDays}d
                              </span>
                            </Tooltip>
                          ) : null}
                          {Number(rule?.autoWithdrawAfterDays || 0) > 0 ? (
                            <Tooltip title={`Auto-withdraw after: ${rule.autoWithdrawAfterDays} days`}>
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/70 border border-white/50 text-textsec">
                                WD {rule.autoWithdrawAfterDays}d
                              </span>
                            </Tooltip>
                          ) : null}

                          <Tooltip title="Step settings (skip + timelines)">
                            <IconButton
                              size="small"
                              onClick={() => openStepSettings(k)}
                              sx={{ ml: 0.5 }}
                            >
                              ⚙
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row) => {
                const withdrawn = isWithdrawn(row);
                const rowId = row.screeningId;

                return (
                  <TableRow
                    key={row.id || rowId}
                    hover={!withdrawn}
                    sx={{
                      opacity: withdrawn ? 0.55 : 1,
                      backgroundColor:
                        lastCreatedId && rowId === lastCreatedId
                          ? "rgba(0, 98, 152, 0.08)"
                          : "transparent",
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 800,
                        color: "var(--aa-primary, #1a2f55)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {rowId}
                    </TableCell>

                    <TableCell
                      onClick={() => startStudyIdEdit(row)}
                      sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      {editingStudyId === rowId ? (
                        <TextField
                          autoFocus
                          size="small"
                          value={studyIdValue}
                          onChange={(e) => setStudyIdValue(e.target.value)}
                          onBlur={saveStudyId}
                          onKeyDown={handleStudyIdKeyDown}
                          inputProps={{ style: { fontSize: 13 } }}
                        />
                      ) : (
                        <span className={row.studyId ? "text-textsec" : "text-textsec/50"}>
                          {row.studyId || "Click to set"}
                        </span>
                      )}
                    </TableCell>

                    <TableCell>
                      {withdrawn ? <Chip size="small" label="Withdrawn" /> : <Chip size="small" label="Active" />}
                    </TableCell>

                    <TableCell
                      onClick={() => !withdrawn && startWithdrawEdit(row)}
                      sx={{ cursor: withdrawn ? "not-allowed" : "pointer", minWidth: 140 }}
                    >
                      {editingWithdrawId === rowId ? (
                        <TextField
                          autoFocus
                          type="date"
                          size="small"
                          value={withdrawValue}
                          onChange={(e) => setWithdrawValue(e.target.value)}
                          onBlur={saveWithdraw}
                          onKeyDown={handleWithdrawKeyDown}
                          inputProps={{ style: { fontSize: 13 } }}
                        />
                      ) : (
                        <span
                          className={
                            formatFirestoreDate(row.withdrewAt) ? "text-textsec" : "text-textsec/50"
                          }
                        >
                          {formatFirestoreDate(row.withdrewAt) || "Click to set"}
                        </span>
                      )}
                    </TableCell>

                    <TableCell sx={{ minWidth: 200 }}>
                      <div className="flex items-center gap-2">
                        {withdrawn ? (
                          <Tooltip title="Revert withdraw (enable editing again)">
                            <button onClick={() => revertWithdraw(row)} className={btnRowPrimary}>
                              Revert
                            </button>
                          </Tooltip>
                        ) : (
                          <span className="text-textsec/50 text-xs">—</span>
                        )}

                        <Tooltip title="Delete this screening">
                          <button onClick={() => openDeleteScreening(rowId)} className={btnRowDanger}>
                            Delete
                          </button>
                        </Tooltip>
                      </div>
                    </TableCell>

                    {stepOrder.map((stepKey) => {
                      // apply skip behavior to display: if canSkip and empty, show next date if available
                      const rule = stepRules?.[stepKey] || {};
                      let cellDate = getStepDateByKey(row, stepKey);

                      if (!cellDate && rule?.canSkip) {
                        // find next non-empty
                        const idx = stepOrder.indexOf(stepKey);
                        for (let j = idx + 1; j < stepOrder.length; j++) {
                          const d = getStepDateByKey(row, stepOrder[j]);
                          if (d) {
                            cellDate = d;
                            break;
                          }
                        }
                      }

                      const isEditing =
                        editing &&
                        editing.id === rowId &&
                        editing.stepKey === String(stepKey);

                      const overdue = getAutoWithdrawStatus(row, stepKey);

                      return (
                        <TableCell
                          key={stepKey}
                          onClick={() => !withdrawn && startEdit(row, stepKey)}
                          sx={{
                            cursor: withdrawn ? "not-allowed" : "pointer",
                            userSelect: "none",
                            color: withdrawn ? "rgba(0,0,0,0.35)" : "inherit",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {isEditing ? (
                            <div className="flex flex-col gap-1">
                              <TextField
                                autoFocus
                                type="date"
                                size="small"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={saveEdit}
                                onKeyDown={handleKeyDown}
                                inputProps={{
                                  min: editing?.minDateISO || "",
                                  max: editing?.maxDateISO || "",
                                  style: { fontSize: 13 },
                                }}
                              />
                              {editing?.warnMinWaitMsg ? (
                                <div className="text-[11px] text-textsec/70">
                                  {editing.warnMinWaitMsg}
                                </div>
                              ) : null}
                              {editing?.maxDateISO ? (
                                <div className="text-[11px] text-textsec/70">
                                  Max allowed (next step already set): {editing.maxDateISO}
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className={cellDate ? "text-textsec" : "text-textsec/50"}>
                                {cellDate || "—"}
                              </span>
                              {overdue ? (
                                <Tooltip
                                  title={`Over auto-withdraw threshold by ${overdue.overdueBy} day(s) (threshold ${overdue.threshold}d). Backend should auto-withdraw.`}
                                >
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/70 border border-primary/20 text-primary">
                                    Overdue
                                  </span>
                                </Tooltip>
                              ) : null}
                            </div>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}

              {!rows.length ? (
                <TableRow>
                  <TableCell colSpan={5 + Math.max(1, stepOrder.length)} align="center" sx={{ py: 6 }}>
                    {loading ? "Loading..." : projectId ? "No records yet" : "Select a project"}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        <div className="mt-4 text-xs text-textsec/70">
          Tip: Press <span className="font-semibold">Esc</span> to cancel edit.
        </div>
      </div>

      {/* Create Project Dialog */}
      <Dialog
        open={projectDialogOpen}
        onClose={() => !creatingProject && setProjectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: dialogPaperClass }}
      >
        <DialogTitle className={dialogTitleClass} sx={{ fontWeight: 800 }}>
          Create project
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
            <TextField
              label="projectId (required)"
              value={newProjectId}
              onChange={(e) => setNewProjectId(e.target.value)}
              placeholder="e.g. studyA_2026"
              autoFocus
              fullWidth
            />
            <TextField
              label="name (optional)"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder="e.g. Hypertension Study"
              fullWidth
            />
            <TextField
              label="number of steps (1–50)"
              type="number"
              value={newProjectStepCount}
              onChange={(e) => setNewProjectStepCount(e.target.value)}
              inputProps={{ min: 1, max: 50 }}
              fullWidth
            />
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              projectId should contain only letters, numbers, dash, underscore.
            </div>
          </div>
        </DialogContent>

        <DialogActions className={dialogActionsClass} sx={{ px: 3, py: 2 }}>
          <button
            onClick={() => setProjectDialogOpen(false)}
            disabled={creatingProject}
            className={btnDialogSecondary}
          >
            Cancel
          </button>
          <button
            onClick={createProject}
            disabled={creatingProject}
            className={btnDialogPrimary}
          >
            {creatingProject ? "Creating..." : "Create"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Create Screening Dialog */}
      <Dialog
        open={screeningDialogOpen}
        onClose={() => !creatingScreening && setScreeningDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: dialogPaperClass }}
      >
        <DialogTitle className={dialogTitleClass} sx={{ fontWeight: 800 }}>
          Add screening
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
            <FormControlLabel
              control={<Checkbox checked={autoIncrement} onChange={(e) => setAutoIncrement(e.target.checked)} />}
              label={
                suggestedNextId
                  ? `Auto increment (next = ${suggestedNextId})`
                  : "Auto increment (fetching next id...)"
              }
            />

            <TextField
              label="screeningId (required)"
              value={newScreeningId}
              onChange={(e) => setNewScreeningId(e.target.value)}
              placeholder={suggestedNextId || "Enter screeningId"}
              disabled={autoIncrement}
              fullWidth
            />

            <TextField
              label="studyId (optional)"
              value={newStudyId}
              onChange={(e) => setNewStudyId(e.target.value)}
              placeholder="Enter studyId"
              fullWidth
            />

            <div style={{ fontSize: 12, opacity: 0.7 }}>
              Turn off Auto increment if you want to type a custom screeningId.
            </div>
          </div>
        </DialogContent>

        <DialogActions className={dialogActionsClass} sx={{ px: 3, py: 2 }}>
          <button
            onClick={() => setScreeningDialogOpen(false)}
            disabled={creatingScreening}
            className={btnDialogSecondary}
          >
            Cancel
          </button>
          <button
            onClick={createNewScreening}
            disabled={creatingScreening || !newScreeningId.trim()}
            className={btnDialogPrimary}
          >
            {creatingScreening ? "Creating..." : "Create"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Edit Alias Dialog */}
      <Dialog
        open={aliasDialogOpen}
        onClose={() => !savingAlias && setAliasDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: dialogPaperClass }}
      >
        <DialogTitle className={dialogTitleClass} sx={{ fontWeight: 800 }}>
          {aliasStepKey ? `Rename ${getStepLabel(aliasStepKey)}` : "Rename step"}
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
            <TextField
              label="Alias (blank = default Step X)"
              value={aliasValue}
              onChange={(e) => setAliasValue(e.target.value)}
              placeholder={aliasStepKey ? getStepLabel(aliasStepKey) : ""}
              autoFocus
              fullWidth
            />
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              This updates the project-level step name and affects CSV headers too.
            </div>
          </div>
        </DialogContent>

        <DialogActions className={dialogActionsClass} sx={{ px: 3, py: 2 }}>
          <button
            onClick={() => setAliasValue("")}
            disabled={savingAlias}
            className={btnDialogSecondary}
          >
            Clear
          </button>
          <button
            onClick={() => setAliasDialogOpen(false)}
            disabled={savingAlias}
            className={btnDialogSecondary}
          >
            Cancel
          </button>
          <button
            onClick={saveAlias}
            disabled={savingAlias}
            className={btnDialogPrimary}
          >
            {savingAlias ? "Saving..." : "Save"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Step Settings Dialog (Skip + timelines) */}
      <Dialog
        open={stepSettingsOpen}
        onClose={() => !savingStepSettings && setStepSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: dialogPaperClass }}
      >
        <DialogTitle className={dialogTitleClass} sx={{ fontWeight: 800 }}>
          {stepSettingsKey ? `Step settings: ${getStepLabel(stepSettingsKey)}` : "Step settings"}
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <div style={{ display: "grid", gap: 14, marginTop: 8 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(stepSettingsDraft.canSkip)}
                  onChange={(e) =>
                    setStepSettingsDraft((p) => ({ ...p, canSkip: e.target.checked }))
                  }
                />
              }
              label="Can skip this step (if empty, it will inherit the next step’s date in views/export)"
            />

            <TextField
              label="Minimum wait days from previous step (locks earliest date)"
              type="number"
              value={stepSettingsDraft.minWaitDays}
              onChange={(e) =>
                setStepSettingsDraft((p) => ({
                  ...p,
                  minWaitDays: clampInt(e.target.value, 0, 3650) ?? 0,
                }))
              }
              inputProps={{ min: 0, max: 3650 }}
              fullWidth
            />

            <TextField
              label="Auto-withdraw after N days without reporting this step (from previous step date)"
              type="number"
              value={stepSettingsDraft.autoWithdrawAfterDays}
              onChange={(e) =>
                setStepSettingsDraft((p) => ({
                  ...p,
                  autoWithdrawAfterDays: clampInt(e.target.value, 0, 3650) ?? 0,
                }))
              }
              inputProps={{ min: 0, max: 3650 }}
              fullWidth
            />

            <div style={{ fontSize: 12, opacity: 0.7 }}>
              Note: Auto-withdraw is shown as an “Overdue” hint in the UI, but backend should enforce actual withdrawal.
            </div>
          </div>
        </DialogContent>

        <DialogActions className={dialogActionsClass} sx={{ px: 3, py: 2 }}>
          <button
            onClick={() => setStepSettingsOpen(false)}
            disabled={savingStepSettings}
            className={btnDialogSecondary}
          >
            Cancel
          </button>
          <button
            onClick={saveStepSettings}
            disabled={savingStepSettings}
            className={btnDialogPrimary}
          >
            {savingStepSettings ? "Saving..." : "Save"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Manage Steps Dialog */}
      <Dialog
        open={manageStepsOpen}
        onClose={() => !savingManageSteps && setManageStepsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: dialogPaperClass }}
      >
        <DialogTitle className={dialogTitleClass} sx={{ fontWeight: 800 }}>
          Manage steps
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
            <div style={{ fontSize: 13, opacity: 0.85 }}>
              Drag steps to reorder. Delete removes the column from the UI (no data deleted).
              Add creates new empty steps.
            </div>

            <Divider />

            <div className="flex items-center gap-2 flex-wrap">
              <TextField
                label="Add how many steps"
                type="number"
                value={manageAddCount}
                onChange={(e) => setManageAddCount(e.target.value)}
                inputProps={{ min: 1, max: 20 }}
                size="small"
                sx={{ maxWidth: 180 }}
              />
              <button onClick={addStepsToDraft} className={btnDialogPrimary}>
                Add step(s)
              </button>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              {manageDraftOrder.map((k, idx) => (
                <div
                  key={k}
                  draggable
                  onDragStart={onDragStart(k)}
                  onDragOver={onDragOver(k)}
                  onDrop={onDrop(k)}
                  className="rounded-xl bg-white/60 border border-white/50 shadow-sm px-3 py-2 flex items-center justify-between"
                  style={{ cursor: "grab" }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-textsec/60">#{idx + 1}</span>
                    <span className="font-semibold text-textsec">{getStepLabel(k, idx)}</span>
                    <span className="text-xs text-textsec/50">(key {k})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => deleteStepFromDraft(k)}
                      className={btnDialogSecondary}
                      disabled={manageDraftOrder.length <= 1}
                      title="Delete step (no data removed)"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ fontSize: 12, opacity: 0.7 }}>
              Adding steps is blocked if any row has a “gap” (a missing earlier step while a later step is already filled).
            </div>
          </div>
        </DialogContent>

        <DialogActions className={dialogActionsClass} sx={{ px: 3, py: 2 }}>
          <button
            onClick={() => setManageStepsOpen(false)}
            disabled={savingManageSteps}
            className={btnDialogSecondary}
          >
            Cancel
          </button>
          <button
            onClick={saveManageSteps}
            disabled={savingManageSteps}
            className={btnDialogPrimary}
          >
            {savingManageSteps ? "Saving..." : "Save changes"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Delete Screening Dialog */}
      <Dialog
        open={deleteScreeningOpen}
        onClose={() => !deletingScreening && setDeleteScreeningOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: dialogPaperClass }}
      >
        <DialogTitle className={dialogTitleClass} sx={{ fontWeight: 800 }}>
          Delete screening
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
            <div style={{ fontSize: 13 }}>
              This will permanently delete screening <b>{deleteScreeningId}</b> from project{" "}
              <b>{projectId}</b>.
            </div>
          </div>
        </DialogContent>

        <DialogActions className={dialogActionsClass} sx={{ px: 3, py: 2 }}>
          <button
            onClick={() => setDeleteScreeningOpen(false)}
            disabled={deletingScreening}
            className={btnDialogSecondary}
          >
            Cancel
          </button>
          <button
            onClick={confirmDeleteScreening}
            disabled={deletingScreening}
            className={btnDialogDanger}
          >
            {deletingScreening ? "Deleting..." : "Delete"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Clear Project Data Dialog */}
      <Dialog
        open={clearProjectOpen}
        onClose={() => !clearingProject && setClearProjectOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: dialogPaperClass }}
      >
        <DialogTitle className={dialogTitleClass} sx={{ fontWeight: 800 }}>
          Clear project data
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
            <div style={{ fontSize: 13 }}>
              This will delete <b>ALL screenings</b> in project <b>{projectId}</b>. The project itself will remain.
            </div>
            <Divider />
            <TextField
              label="Type CLEAR to confirm"
              value={clearConfirmText}
              onChange={(e) => setClearConfirmText(e.target.value)}
              autoFocus
              fullWidth
            />
          </div>
        </DialogContent>

        <DialogActions className={dialogActionsClass} sx={{ px: 3, py: 2 }}>
          <button
            onClick={() => setClearProjectOpen(false)}
            disabled={clearingProject}
            className={btnDialogSecondary}
          >
            Cancel
          </button>
          <button
            onClick={confirmClearProject}
            disabled={clearingProject || clearConfirmText.trim().toUpperCase() !== "CLEAR"}
            className={btnDialogDanger}
          >
            {clearingProject ? "Clearing..." : "Clear"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Delete Project Dialog */}
      <Dialog
        open={deleteProjectOpen}
        onClose={() => !deletingProject && setDeleteProjectOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: dialogPaperClass }}
      >
        <DialogTitle className={dialogTitleClass} sx={{ fontWeight: 800 }}>
          Delete project
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
            <div style={{ fontSize: 13 }}>
              This will permanently delete project <b>{projectId}</b> and <b>ALL its screenings</b>.
            </div>
            <Divider />
            <TextField
              label="Type DELETE to confirm"
              value={deleteProjectConfirmText}
              onChange={(e) => setDeleteProjectConfirmText(e.target.value)}
              autoFocus
              fullWidth
            />
          </div>
        </DialogContent>

        <DialogActions className={dialogActionsClass} sx={{ px: 3, py: 2 }}>
          <button
            onClick={() => setDeleteProjectOpen(false)}
            disabled={deletingProject}
            className={btnDialogSecondary}
          >
            Cancel
          </button>
          <button
            onClick={confirmDeleteProject}
            disabled={deletingProject || deleteProjectConfirmText.trim().toUpperCase() !== "DELETE"}
            className={btnDialogDanger}
          >
            {deletingProject ? "Deleting..." : "Delete project"}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}